import { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { setAuthToken } from '../services/api';

export const AuthContext = createContext(null);

const USER_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastFetchTime = useRef(null);
  const navigate = useNavigate();

  const fetchUser = useCallback(async (force = false) => {
    // If we have fresh user data and aren't being forced to refresh, skip the fetch
    if (!force && user && lastFetchTime.current && 
        (Date.now() - lastFetchTime.current < USER_CACHE_TTL)) {
        setLoading(false);
        return;
    }

    const token = localStorage.getItem('token');
    console.log('fetchUser - token from storage:', token ? 'exists' : 'missing');
    if (!token) {
        setLoading(false);
        return;
    }

    // Skip the /me call during verification since we already have the user data
    if (sessionStorage.getItem('justLoggedIn')) {
        setLoading(false);
        return;
    }

    try {
        setAuthToken(token); // Ensure token is set in API headers
        const response = await api.get('/auth/me');
        setUser(response.data.user);
        lastFetchTime.current = Date.now();
    } catch (error) {
        // Only clear token and redirect if we're not in the verification process
        if (!sessionStorage.getItem('justLoggedIn')) {
            setAuthToken(null);
            setUser(null);
            navigate('/login');
        } else {
            // If we're in verification process, keep the user data
            console.log('Keeping user data during verification');
        }
    } finally {
        setLoading(false);
    }
  }, [navigate, user]);

  // Initial load
  useEffect(() => {
    const initializeAuth = async () => {
      // If we're in the verification process, skip the initial load
      if (sessionStorage.getItem('justLoggedIn')) {
        setLoading(false);
        return;
      }

      await fetchUser();
    };

    initializeAuth();
  }, [fetchUser]);

  const login = useCallback(async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      
      if (response.data.success) {
        // If we have a token, it means we've verified a magic link
        if (response.data.token) {
          setAuthToken(response.data.token);
          setUser(response.data.user);
          lastFetchTime.current = Date.now();
          sessionStorage.setItem('justLoggedIn', 'true');
          navigate('/dashboard');
          return { success: true };
        }
        
        // If we have emails for selection, return them for the email selection dialog
        if (response.data.requiresEmailSelection && response.data.emails) {
          return {
            success: true,
            requiresEmailSelection: true,
            emails: response.data.emails
          };
        }
        
        // If we don't have email options, the magic link was sent to the only available email
        return response.data;
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }, [navigate]);

  const verifyMagicLink = useCallback(async (token) => {
    try {
      const response = await api.post('/auth/verify-magic-link', { token });
      
      if (response.data.success) {        
        // Set session flag BEFORE setting token and user
        sessionStorage.setItem('justLoggedIn', 'true');
        
        // Set the token and user data
        const authToken = response.data.token;
        setAuthToken(authToken);
        const userData = response.data.user;
        setUser(userData);
        lastFetchTime.current = Date.now();
        
        // Navigate to dashboard
        navigate('/dashboard');
        
        // Clear the justLoggedIn flag after a delay
        setTimeout(() => {
          sessionStorage.removeItem('justLoggedIn');
        }, 2000);
        
        return { success: true };
      } else {
        return {
          success: false,
          message: response.data.message || 'Verification failed'
        };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'An error occurred during verification'
      };
    }
  }, [navigate]);

  const logout = useCallback(async () => {
    try {
      // Call server logout endpoint
      await api.post('/auth/logout');
    } catch (error) {
      console.log('Error during logout');
    } finally {
      // Clear client-side data regardless of server response
      setAuthToken(null);
      setUser(null);
      lastFetchTime.current = null;
      // Clear all sessionStorage items
      sessionStorage.clear();
      navigate('/');
    }
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      loading, 
      verifyMagicLink,
      refreshUser: () => fetchUser(true) 
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 